import { apiClient } from '../api-client';
import type {
  EvvColumnMapping,
  EvvExceptionReason,
  EvvMissedPromptVisit,
  EvvOverrideResult,
  EvvPatientMapResult,
  EvvReconciliationRun,
  EvvReconciliationRunDetail,
  EvvReconciliationSummary,
  EvvResolutionAction,
  EvvRunItem,
  EvvRunResponse,
  EvvUploadPreview,
  EvvUsageScore,
  PatientPendingEvv
} from '../../dtos/evv/reconciliation.dto';

export type {
  EvvBucket,
  EvvColumnField,
  EvvColumnMapping,
  EvvExceptionReason,
  EvvMissedPromptVisit,
  EvvOverrideResult,
  EvvPatientMapResult,
  EvvReconciliationRun,
  EvvReconciliationRunDetail,
  EvvReconciliationSummary,
  EvvResolutionAction,
  EvvRunItem,
  EvvRunResponse,
  EvvUploadPreview,
  EvvUsageScore,
  PatientPendingEvv
} from '../../dtos/evv/reconciliation.dto';

/**
 * Customer-facing labels for reconciliation buckets (PRD §5.13 vocabulary).
 * The bucket enum stays internal; never show it raw.
 */
const BUCKET_LABELS: Record<string, string> = {
  MATCHED: 'Matched',
  DURATION_MISMATCH: 'Duration differs',
  HHAX_ONLY: 'In HHAX only',
  BLACKBIRD_ONLY: 'In Pelora only (no HHAX clock-in)',
  HHAX_UNVERIFIED: 'HHAX unverified',
  MANUAL_REVIEW: 'Needs manual review',
  INFORMATIONAL: 'Matched (EVV optional)',
};

/** Label for a reconciliation bucket. Unknown values are title-cased. */
export function bucketLabel(bucket: string | null | undefined): string {
  if (!bucket) return '';
  const known = BUCKET_LABELS[bucket];
  if (known) return known;
  return bucket
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const EXCEPTION_REASONS: Array<{ value: EvvExceptionReason; label: string }> = [
  { value: 'NURSE_NO_CLOCKIN', label: 'Nurse did not clock in' },
  { value: 'HHAX_OUTAGE', label: 'HHAX outage' },
  { value: 'DEVICE_ISSUE', label: 'Device issue' },
  { value: 'MANUAL_VISIT_HHAX', label: 'Manual visit entered in HHAX (verify on next export)' },
  { value: 'OTHER', label: 'Other (note required)' },
];

export interface EvvUploadOptions {
  dryRun?: boolean;
  acknowledgeOutOfRange?: boolean;
  toleranceMinutes?: number;
  columnMapping?: EvvColumnMapping;
}

function csvForm(file: File, fields: Record<string, string | undefined>): FormData {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined) formData.append(key, value);
  });
  formData.append('file', file);
  return formData;
}

function requireData<T>(data: T | undefined, message: string): T {
  if (data === undefined || data === null) throw new Error(message);
  return data;
}

export class EvvReconciliationService {
  /** EVV-02/04/18 screen header. */
  static async getSummary(): Promise<EvvReconciliationSummary> {
    const response = await apiClient.get<EvvReconciliationSummary>('/evv/reconciliations/summary');
    return requireData(response.data, 'Failed to load the reconciliation summary');
  }

  /** Check the file without changing anything (EVV-05/06/07). Errors throw ApiError with domainCode. */
  static async previewCsv(file: File, options: Omit<EvvUploadOptions, 'dryRun' | 'acknowledgeOutOfRange'> = {}): Promise<EvvUploadPreview> {
    const response = await apiClient.upload<EvvUploadPreview>('/evv/reconciliations', csvForm(file, {
      dryRun: 'true',
      toleranceMinutes: options.toleranceMinutes === undefined ? undefined : String(options.toleranceMinutes),
      columnMapping: options.columnMapping ? JSON.stringify(options.columnMapping) : undefined,
    }));
    return requireData(response.data, 'Failed to check the file');
  }

  /** Run the reconciliation. acknowledgeOutOfRange confirms a CSV that misses pending dates. */
  static async uploadCsv(file: File, options: EvvUploadOptions = {}): Promise<EvvRunResponse> {
    const response = await apiClient.upload<EvvRunResponse>('/evv/reconciliations', csvForm(file, {
      acknowledgeOutOfRange: options.acknowledgeOutOfRange ? 'true' : undefined,
      toleranceMinutes: options.toleranceMinutes === undefined ? undefined : String(options.toleranceMinutes),
      columnMapping: options.columnMapping ? JSON.stringify(options.columnMapping) : undefined,
    }), { timeout: 60000 });
    return requireData(response.data, 'Failed to run the reconciliation');
  }

  static async listRuns(limit: number = 50): Promise<EvvReconciliationRun[]> {
    const response = await apiClient.get<{ items: EvvReconciliationRun[] }>('/evv/reconciliations', { limit });
    return response.data && Array.isArray(response.data.items) ? response.data.items : [];
  }

  static async getRun(reconciliationId: string): Promise<EvvReconciliationRunDetail> {
    const response = await apiClient.get<EvvReconciliationRunDetail>(`/evv/reconciliations/${encodeURIComponent(reconciliationId)}`);
    const run = requireData(response.data, 'Failed to load the reconciliation run');
    return { ...run, items: run.items || [], unresolvedItems: run.unresolvedItems || [] };
  }

  static async resolveItem(
    reconciliationId: string,
    itemId: string,
    payload: { action: EvvResolutionAction; reasonCode?: EvvExceptionReason; note?: string; peloraVisitId?: string }
  ): Promise<EvvRunItem> {
    const response = await apiClient.put<{ item: EvvRunItem }>(
      `/evv/reconciliations/${encodeURIComponent(reconciliationId)}/items/${encodeURIComponent(itemId)}/resolve`,
      payload
    );
    return requireData(response.data?.item, 'Failed to resolve the item');
  }

  static async downloadUnresolvedCsv(reconciliationId: string): Promise<void> {
    const blob = await apiClient.download(`/evv/reconciliations/${encodeURIComponent(reconciliationId)}/unresolved.csv`);
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = `evv-unresolved-${reconciliationId}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(downloadUrl);
  }

  static async listFlaggedVisits(): Promise<EvvMissedPromptVisit[]> {
    const response = await apiClient.get<{ items: EvvMissedPromptVisit[] }>('/evv/flagged-visits');
    return response.data && Array.isArray(response.data.items) ? response.data.items : [];
  }

  static async interveneFlaggedVisit(visitId: string, action: 'MARK_RESOLVED' | 'MARK_UNABLE_TO_RESOLVE', note?: string): Promise<void> {
    await apiClient.put(`/evv/flagged-visits/${encodeURIComponent(visitId)}/intervention`, { action, note: note || undefined });
  }

  /** 1.8 Admin-only override: reason 10–500 characters, minutes 1..documented visit minutes. */
  static async overrideVisit(
    patientId: string,
    visitId: string,
    payload: { reason: string; evvVerifiedMinutes?: number }
  ): Promise<EvvOverrideResult> {
    const response = await apiClient.put<EvvOverrideResult>(
      `/patientmr/${encodeURIComponent(patientId)}/visits/${encodeURIComponent(visitId)}/evv-override`,
      payload
    );
    return requireData(response.data, 'Failed to apply the EVV override');
  }

  static async getUsageScore(range?: { from: string; to: string }): Promise<EvvUsageScore> {
    const response = await apiClient.get<EvvUsageScore>('/evv/usage-score', range ? { from: range.from, to: range.to } : undefined);
    return requireData(response.data, 'Failed to load the EVV usage score');
  }

  /** EVV-15: HHAX patient demographics export -> HHAX patient IDs on Pelora patients. */
  static async importPatientMap(file: File, options: { overwrite?: boolean } = {}): Promise<EvvPatientMapResult> {
    const response = await apiClient.upload<EvvPatientMapResult>('/evv/patient-map', csvForm(file, {
      overwrite: options.overwrite ? 'true' : undefined,
    }), { timeout: 60000 });
    return requireData(response.data, 'Failed to import HHAX patient IDs');
  }

  /** DSC-03: a patient's EVV-required visits still PENDING. */
  static async getPatientPendingEvv(patientId: string): Promise<PatientPendingEvv> {
    const response = await apiClient.get<PatientPendingEvv>(`/patientmr/${encodeURIComponent(patientId)}/evv/pending`);
    return requireData(response.data, 'Failed to load pending EVV visits');
  }
}
